import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KnowledgeDocument,
  KnowledgeProcessingStatus,
  KnowledgeVisibility,
  isStructuredKnowledgeDocument,
} from '../types/Knowledge';
import { KnowledgeDocumentAttachmentsPanel } from './KnowledgeDocumentAttachmentsPanel';
import styles from './KnowledgeDocumentCard.module.css';

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const truncatePublicUrl = (url: string, maxLength = 42): string => {
  if (!url || url.length <= maxLength) {
    return url;
  }

  return `${url.slice(0, maxLength - 1)}…`;
};

const getFileKind = (document: KnowledgeDocument): { label: string; tone: string } => {
  const mime = document.mimeType.toLowerCase();
  const name = document.originalFileName.toLowerCase();

  if (mime.includes('pdf') || name.endsWith('.pdf')) {
    return { label: 'PDF', tone: styles.fileKindPdf };
  }

  if (mime.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) {
    return { label: 'DOC', tone: styles.fileKindDoc };
  }

  if (mime.includes('json') || name.endsWith('.json')) {
    return { label: 'JSON', tone: styles.fileKindJson };
  }

  if (mime.startsWith('image/')) {
    return { label: 'IMG', tone: styles.fileKindImage };
  }

  return { label: 'TXT', tone: styles.fileKindText };
};

export interface KnowledgeDocumentCardProps {
  document: KnowledgeDocument;
  canManage: boolean;
  rowBusy: boolean;
  attachmentsExpanded: boolean;
  categoryLabel: string;
  editingTitleId: string;
  titleDraft: string;
  editingTagsId: string;
  tagsDraft: string;
  editingSummaryId: string;
  summaryDraft: string;
  onStartEditTitle: (document: KnowledgeDocument) => void;
  onSaveTitle: (documentId: string) => void;
  onCancelEditTitle: () => void;
  onTitleDraftChange: (value: string) => void;
  onStartEditTags: (document: KnowledgeDocument) => void;
  onSaveTags: (documentId: string) => void;
  onCancelEditTags: () => void;
  onTagsDraftChange: (value: string) => void;
  onStartEditSummary: (document: KnowledgeDocument) => void;
  onSaveSummary: (documentId: string) => void;
  onCancelEditSummary: () => void;
  onSummaryDraftChange: (value: string) => void;
  onChangeVisibility: (document: KnowledgeDocument, visibility: KnowledgeVisibility) => void;
  onToggleAttachments: (documentId: string) => void;
  onReplaceFile: (document: KnowledgeDocument, file: File) => void;
  onReprocess: (documentId: string) => void;
  onDelete: (document: KnowledgeDocument) => void;
  onViewActivity: (document: KnowledgeDocument) => void;
  onCopyPublicUrl: (publicUrl: string) => void;
  onAttachmentCountChange: (documentId: string, count: number) => void;
}

export const KnowledgeDocumentCard: React.FC<KnowledgeDocumentCardProps> = ({
  document,
  canManage,
  rowBusy,
  attachmentsExpanded,
  categoryLabel,
  editingTitleId,
  titleDraft,
  editingTagsId,
  tagsDraft,
  editingSummaryId,
  summaryDraft,
  onStartEditTitle,
  onSaveTitle,
  onCancelEditTitle,
  onTitleDraftChange,
  onStartEditTags,
  onSaveTags,
  onCancelEditTags,
  onTagsDraftChange,
  onStartEditSummary,
  onSaveSummary,
  onCancelEditSummary,
  onSummaryDraftChange,
  onChangeVisibility,
  onToggleAttachments,
  onReplaceFile,
  onReprocess,
  onDelete,
  onViewActivity,
  onCopyPublicUrl,
  onAttachmentCountChange,
}) => {
  const { t } = useTranslation(['knowledge', 'common']);
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const fileKind = getFileKind(document);
  const isStructured = isStructuredKnowledgeDocument(document);
  const attachmentCount = document.attachmentCount ?? 0;

  const renderVisibilityBadge = (value: KnowledgeVisibility) => {
    const label = value === 'customer_safe'
      ? t('customerSafe', { ns: 'knowledge' })
      : value === 'public'
        ? t('visibleToAll', { ns: 'knowledge' })
        : t('internalOnly', { ns: 'knowledge' });
    const cls = value === 'customer_safe'
      ? styles.badgeCustomerSafe
      : value === 'public'
        ? styles.badgePublic
        : styles.badgeInternal;

    return <span className={`${styles.badge} ${cls}`}>{label}</span>;
  };

  const renderStatusBadge = (value: KnowledgeProcessingStatus) => {
    const className = value === 'ready'
      ? styles.badgeReady
      : value === 'failed'
        ? styles.badgeFailed
        : styles.badgeProcessing;

    return (
      <span className={`${styles.badge} ${className}`}>
        {t(`status.${value}`, { ns: 'knowledge' })}
      </span>
    );
  };

  return (
    <article className={`${styles.card} ${rowBusy ? styles.cardBusy : ''}`}>
      <div className={styles.cardMain}>
        <div className={`${styles.fileKind} ${fileKind.tone}`} aria-hidden>
          {fileKind.label}
        </div>

        <div className={styles.cardContent}>
          <div className={styles.cardHead}>
            <div className={styles.titleBlock}>
              {canManage && editingTitleId === document.id ? (
                <div className={styles.inlineEdit}>
                  <input
                    className={styles.textField}
                    type="text"
                    maxLength={256}
                    value={titleDraft}
                    onChange={(event) => onTitleDraftChange(event.target.value)}
                    disabled={rowBusy}
                  />
                  <div className={styles.inlineEditActions}>
                    <button type="button" className="btn btn-primary" onClick={() => onSaveTitle(document.id)} disabled={rowBusy}>
                      {rowBusy ? t('working', { ns: 'knowledge' }) : t('save', { ns: 'common' })}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={onCancelEditTitle} disabled={rowBusy}>
                      {t('cancel', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              ) : canManage ? (
                <button
                  type="button"
                  className={styles.titleButton}
                  onClick={() => onStartEditTitle(document)}
                  disabled={rowBusy}
                  title={t('editTitle', { ns: 'knowledge' })}
                >
                  {document.title || document.originalFileName}
                </button>
              ) : (
                <h4 className={styles.titleText}>{document.title || document.originalFileName}</h4>
              )}
              <p className={styles.fileMeta}>
                {document.originalFileName} · {formatFileSize(document.fileSize)}
              </p>
            </div>

            <div className={styles.badgeRow}>
              {renderStatusBadge(document.processingStatus)}
              {canManage ? (
                <select
                  className={styles.visibilitySelect}
                  value={document.visibility}
                  disabled={rowBusy}
                  onChange={(event) => onChangeVisibility(document, event.target.value as KnowledgeVisibility)}
                  aria-label={t('visibility', { ns: 'knowledge' })}
                >
                  <option value="internal">{t('internalOnly', { ns: 'knowledge' })}</option>
                  <option value="customer_safe">{t('customerSafe', { ns: 'knowledge' })}</option>
                  <option value="public">{t('visibleToAll', { ns: 'knowledge' })}</option>
                </select>
              ) : renderVisibilityBadge(document.visibility)}
            </div>
          </div>

          <div className={styles.metaRow}>
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('fileCategoryLabel', { ns: 'knowledge' })}</span>
              <span>{categoryLabel}</span>
            </span>
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>{t('updated', { ns: 'knowledge' })}</span>
              <span>{formatDateTime(document.updatedAt)}</span>
            </span>
            {!isStructured && attachmentCount > 0 ? (
              <span className={styles.imageCountBadge}>
                {t('attachments.manageImages', { ns: 'knowledge', count: attachmentCount })}
              </span>
            ) : null}
          </div>

          <div className={styles.tagsSection}>
            <span className={styles.metaLabel}>{t('tags', { ns: 'knowledge' })}</span>
            {canManage && editingTagsId === document.id ? (
              <div className={styles.inlineEdit}>
                <input
                  className={styles.textField}
                  type="text"
                  value={tagsDraft}
                  onChange={(event) => onTagsDraftChange(event.target.value)}
                  placeholder={t('tagsPlaceholder', { ns: 'knowledge' })}
                  disabled={rowBusy}
                />
                <div className={styles.inlineEditActions}>
                  <button type="button" className="btn btn-primary" onClick={() => onSaveTags(document.id)} disabled={rowBusy}>
                    {rowBusy ? t('working', { ns: 'knowledge' }) : t('save', { ns: 'common' })}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={onCancelEditTags} disabled={rowBusy}>
                    {t('cancel', { ns: 'common' })}
                  </button>
                </div>
              </div>
            ) : canManage ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => onStartEditTags(document)}
                disabled={rowBusy}
              >
                {document.tags.length > 0 ? (
                  <span className={styles.tagList}>
                    {document.tags.map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
                  </span>
                ) : (
                  <span className={styles.placeholderText}>{t('addTags', { ns: 'knowledge' })}</span>
                )}
              </button>
            ) : document.tags.length > 0 ? (
              <span className={styles.tagList}>
                {document.tags.map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
              </span>
            ) : (
              <span className={styles.placeholderText}>—</span>
            )}
          </div>

          <div className={styles.summarySection}>
            <span className={styles.metaLabel}>{t('summary', { ns: 'knowledge' })}</span>
            {canManage && editingSummaryId === document.id ? (
              <div className={styles.inlineEdit}>
                <textarea
                  className={styles.textArea}
                  rows={3}
                  maxLength={2000}
                  value={summaryDraft}
                  onChange={(event) => onSummaryDraftChange(event.target.value)}
                  disabled={rowBusy}
                />
                <div className={styles.inlineEditActions}>
                  <button type="button" className="btn btn-primary" onClick={() => onSaveSummary(document.id)} disabled={rowBusy}>
                    {rowBusy ? t('working', { ns: 'knowledge' }) : t('save', { ns: 'common' })}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={onCancelEditSummary} disabled={rowBusy}>
                    {t('cancel', { ns: 'common' })}
                  </button>
                </div>
              </div>
            ) : canManage ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => onStartEditSummary(document)}
                disabled={rowBusy}
              >
                {document.summary ? (
                  <span className={styles.summaryText}>{document.summary}</span>
                ) : (
                  <span className={styles.placeholderText}>{t('addSummary', { ns: 'knowledge' })}</span>
                )}
              </button>
            ) : (
              <span className={styles.summaryText}>{document.summary || '—'}</span>
            )}
          </div>

          {document.publicUrl ? (
            <div className={styles.publicUrlRow}>
              <a href={document.publicUrl} target="_blank" rel="noreferrer" className={styles.publicUrlLink} title={document.publicUrl}>
                {truncatePublicUrl(document.publicUrl)}
              </a>
              <button type="button" className="btn btn-secondary" onClick={() => void onCopyPublicUrl(document.publicUrl)} disabled={rowBusy}>
                {t('copyPublicUrl', { ns: 'knowledge' })}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.cardActions}>
        {document.publicUrl ? (
          <a href={document.publicUrl} target="_blank" rel="noreferrer" className="btn btn-secondary">
            {t('open', { ns: 'knowledge' })}
          </a>
        ) : null}
        {canManage && !isStructured ? (
          <button
            type="button"
            className={`btn btn-secondary ${attachmentsExpanded ? styles.actionActive : ''}`}
            onClick={() => onToggleAttachments(document.id)}
            disabled={rowBusy}
          >
            {attachmentsExpanded
              ? t('attachments.hideImages', { ns: 'knowledge' })
              : t('attachments.manageImages', { ns: 'knowledge', count: attachmentCount })}
          </button>
        ) : null}
        {canManage && !isStructured ? (
          <button type="button" className="btn btn-secondary" onClick={() => void onReprocess(document.id)} disabled={rowBusy}>
            {rowBusy ? t('working', { ns: 'knowledge' }) : t('reprocess', { ns: 'knowledge' })}
          </button>
        ) : null}
        {canManage ? (
          <>
            <input
              ref={replaceFileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              accept={isStructured ? '.json,application/json' : '.pdf,.doc,.docx,.txt,.md,.json,application/json'}
              onChange={(event) => {
                const nextFile = event.target.files?.[0];
                event.target.value = '';
                if (nextFile) {
                  void onReplaceFile(document, nextFile);
                }
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => replaceFileInputRef.current?.click()}
              disabled={rowBusy}
              title={t('updateFileHelp', { ns: 'knowledge' })}
            >
              {rowBusy ? t('working', { ns: 'knowledge' }) : t('updateFile', { ns: 'knowledge' })}
            </button>
          </>
        ) : null}
        <button type="button" className="btn btn-secondary" onClick={() => onViewActivity(document)} disabled={rowBusy}>
          {t('activity.viewForFile', { ns: 'knowledge' })}
        </button>
        {canManage ? (
          <button
            type="button"
            className={`btn btn-secondary ${styles.deleteAction}`}
            onClick={() => void onDelete(document)}
            disabled={rowBusy}
          >
            {t('delete', { ns: 'common' })}
          </button>
        ) : null}
      </div>

      {attachmentsExpanded ? (
        <KnowledgeDocumentAttachmentsPanel
          document={document}
          canManage={canManage}
          onAttachmentCountChange={onAttachmentCountChange}
        />
      ) : null}
    </article>
  );
};

export default KnowledgeDocumentCard;
